// Per-PR linked-incidents chip cluster. Renders one pill per incident with:
//   * Source icon (Confluence postmortem vs PagerDuty status-page)
//   * Truncated title (~50 chars)
//   * 5-star confidence indicator (filled stars = Math.round(confidence/20))
//   * Reason label (small, muted)
//   * Click-through wraps the chip in an <a> to source_url
//   * Optional sibling pill linking to referenced_pd_alarm_url
//
// Loop design system has no amber palette; we use --loop-yellow-800 for
// filled stars and --loop-neutral-400 for empty. The pill itself uses the
// same neutral-200 background as other badges (StatusBadge pattern).
//
// Source icons are inline SVG (Atlassian Confluence 2017 mark, PagerDuty
// icon). React useId() generates unique gradient IDs per instance so
// multiple chips on the same page don't collide on the Confluence gradient.

import { useId } from 'react';
import type { IncidentLinkReason } from '@death-star/shared';

export interface LinkedIncident {
  incident_id: string;
  confidence: number;
  reason: IncidentLinkReason;
  linked_at: string;
  incident: {
    title: string;
    source: 'confluence-postmortem' | 'pagerduty-statuspage';
    source_url: string;
    occurred_at: string;
    severity: string | null;
    referenced_pd_alarm_url: string | null;
    arch_group: 'edge' | 'engine' | 'hero' | null;
  };
}

// Keyed by the shared taxonomy, so adding a reason fails to compile here
// until it is given a label. The `?? link.reason` fallback at the call sites
// stays as belt-and-braces for a row the API somehow lets through unlabelled.
const REASON_LABEL: Record<IncidentLinkReason, string> = {
  explicit_pr_url: 'explicit PR URL',
  explicit_pr_shorthand: 'explicit PR shorthand',
  business_service_match_via_status_page: 'business service match',
  service_match_via_pm_component: 'service match (PM component)',
  arch_group_only: 'arch group only',
  timing_only: 'timing only',
};

function truncate(s: string, max = 50): string {
  if (s.length <= max) return s;
  return s.slice(0, max - 1) + '…';
}

function ConfidenceStars({ confidence }: { confidence: number }) {
  // confidence is 0-100; map to 0-5 stars, rounded.
  const stars = Math.max(0, Math.min(5, Math.round(confidence / 20)));
  return (
    <span
      title={`${confidence}% confidence`}
      style={{ fontSize: '0.75rem', letterSpacing: '1px', lineHeight: 1 }}
      aria-label={`${stars} of 5 stars confidence`}
    >
      {Array.from({ length: 5 }).map((_, i) => (
        <span
          key={i}
          style={{ color: i < stars ? 'var(--loop-yellow-800)' : 'var(--loop-neutral-400)' }}
        >
          ★
        </span>
      ))}
    </span>
  );
}

// Atlassian Confluence 2017 logo mark (icon-only, two overlapping gradient
// shapes). Source: https://commons.wikimedia.org/wiki/File:Atlassian_Confluence_2017_logo.svg
function ConfluenceIcon({ size = 14 }: { size?: number }) {
  const idA = useId();
  const idB = useId();
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 64 64"
      role="img"
      aria-label="Confluence"
    >
      <defs>
        <linearGradient id={idA} x1="59.68" y1="67.65" x2="20.35" y2="45.05" gradientUnits="userSpaceOnUse">
          <stop offset="0.18" stopColor="#0052cc" />
          <stop offset="1" stopColor="#2684ff" />
        </linearGradient>
        <linearGradient
          id={idB}
          x1="279.76"
          y1="-1616.34"
          x2="240.42"
          y2="-1638.95"
          gradientTransform="translate(282.83 -1616.71) rotate(180)"
          href={`#${idA}`}
        />
      </defs>
      <path
        fill={`url(#${idA})`}
        d="M2.23,49.53c-.65,1.06-1.38,2.29-2,3.27a2,2,0,0,0,.67,2.72l13,8a2,2,0,0,0,2.77-.68c.52-.87,1.19-2,1.92-3.21,5.15-8.5,10.33-7.46,19.67-3l12.89,6.13a2,2,0,0,0,2.69-1l6.19-14a2,2,0,0,0-1-2.62c-2.72-1.28-8.13-3.83-13-6.18C28.51,30.45,13.62,31,2.23,49.53Z"
      />
      <path
        fill={`url(#${idB})`}
        d="M60.52,17.76c.65-1.06,1.38-2.29,2-3.27a2,2,0,0,0-.67-2.72l-13-8A2,2,0,0,0,46,4.43c-.52.87-1.19,2-1.92,3.21-5.15,8.5-10.33,7.46-19.67,3L11.56,4.54a2,2,0,0,0-2.69,1l-6.19,14a2,2,0,0,0,1,2.62c2.72,1.28,8.13,3.83,13,6.18C34.24,36.84,49.13,36.27,60.52,17.76Z"
      />
    </svg>
  );
}

// PagerDuty "PD" mark (icon-only). Source: https://www.vectorlogo.zone/logos/pagerduty/
function PagerDutyIcon({ size = 14 }: { size?: number }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 64 64"
      role="img"
      aria-label="PagerDuty"
    >
      <path
        fill="#06ac38"
        d="M6.704 59.217H0v-33.65c0-3.455 1.418-5.544 2.604-6.704 2.63-2.58 6.2-2.656 6.782-2.656h10.546c3.765 0 5.93 1.52 7.117 2.8 2.346 2.553 2.372 5.853 2.32 6.73v12.687c0 3.662-1.496 5.828-2.733 6.988-2.553 2.398-5.93 2.45-6.73 2.424H6.704zm13.46-18.102c.36 0 1.367-.103 1.908-.62.413-.387.62-1.083.62-2.1v-13.02c0-.36-.077-1.315-.593-1.857-.5-.516-1.444-.62-2.166-.62h-10.6c-2.63 0-2.63 1.985-2.63 2.656v15.55zM57.296 4.783H64V38.46c0 3.455-1.418 5.544-2.604 6.704-2.63 2.58-6.2 2.656-6.782 2.656H44.068c-3.765 0-5.93-1.52-7.117-2.8-2.346-2.553-2.372-5.853-2.32-6.73V25.62c0-3.662 1.496-5.828 2.733-6.988 2.553-2.398 5.93-2.45 6.73-2.424h13.202zM43.836 22.9c-.36 0-1.367.103-1.908.62-.413.387-.62 1.083-.62 2.1v13.02c0 .36.077 1.315.593 1.857.5.516 1.444.62 2.166.62h10.598c2.656-.026 2.656-2 2.656-2.682V22.9z"
      />
    </svg>
  );
}

function SourceIcon({ source }: { source: LinkedIncident['incident']['source'] }) {
  if (source === 'pagerduty-statuspage') {
    return (
      <span title="PagerDuty status page" aria-label="PagerDuty status page" style={{ display: 'inline-flex', lineHeight: 1 }}>
        <PagerDutyIcon />
      </span>
    );
  }
  // confluence-postmortem is the default — covers any unknown future enum value too
  return (
    <span title="Confluence postmortem" aria-label="Confluence postmortem" style={{ display: 'inline-flex', lineHeight: 1 }}>
      <ConfluenceIcon />
    </span>
  );
}

function IncidentChip({ link }: { link: LinkedIncident }) {
  const { incident } = link;
  return (
    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
      <a
        href={incident.source_url}
        target="_blank"
        rel="noreferrer"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          padding: '4px 10px',
          borderRadius: '999px',
          background: 'var(--loop-neutral-200)',
          color: 'var(--loop-neutral-1000)',
          textDecoration: 'none',
          fontSize: '0.8125rem',
          fontWeight: 528,
          border: '1px solid var(--loop-neutral-300)',
        }}
        title={`${incident.title} — ${link.confidence}% confidence (${REASON_LABEL[link.reason] ?? link.reason})`}
      >
        <SourceIcon source={incident.source} />
        <span>{truncate(incident.title, 50)}</span>
        <ConfidenceStars confidence={link.confidence} />
        <span style={{ color: 'var(--loop-neutral-700)', fontSize: '0.6875rem', fontWeight: 400 }}>
          {REASON_LABEL[link.reason] ?? link.reason}
        </span>
      </a>
      {incident.referenced_pd_alarm_url && (
        <a
          href={incident.referenced_pd_alarm_url}
          target="_blank"
          rel="noreferrer"
          title="Open referenced PagerDuty alarm"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            padding: '4px 8px',
            borderRadius: '999px',
            background: 'var(--loop-red-100)',
            color: 'var(--loop-red-700)',
            textDecoration: 'none',
            fontSize: '0.75rem',
            fontWeight: 528,
            border: '1px solid var(--loop-red-200)',
          }}
        >
          <PagerDutyIcon size={12} />
          <span>PD alarm</span>
        </a>
      )}
    </span>
  );
}

export interface LinkedIncidentsProps {
  incidents: LinkedIncident[];
  includeLowConfidence: boolean;
  onToggleLowConfidence: () => void;
}

export function LinkedIncidents({ incidents, includeLowConfidence, onToggleLowConfidence }: LinkedIncidentsProps) {
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontSize: '0.75rem', color: 'var(--loop-neutral-700)' }}>
          {incidents.length === 0
            ? 'No linked incidents.'
            : `${incidents.length} linked incident${incidents.length === 1 ? '' : 's'}`}
        </span>
        <button
          type="button"
          onClick={onToggleLowConfidence}
          aria-pressed={includeLowConfidence}
          aria-label={includeLowConfidence ? 'Hide low-confidence incidents' : 'Show low-confidence incidents (below 50% confidence)'}
          style={{
            background: 'transparent',
            border: '1px solid var(--loop-neutral-300)',
            borderRadius: 4,
            padding: '2px 8px',
            fontSize: '0.6875rem',
            color: 'var(--loop-neutral-800)',
            cursor: 'pointer',
          }}
        >
          {includeLowConfidence ? 'Hide low-confidence (<50)' : 'Show low-confidence (<50)'}
        </button>
      </div>
      {incidents.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {incidents.map((link) => (
            <IncidentChip key={link.incident_id} link={link} />
          ))}
        </div>
      )}
    </div>
  );
}
